'use client';

import React, { useCallback, useEffect, useState } from 'react';
import { RiArrowUpDownFill } from 'react-icons/ri';
import { useDebounce } from 'use-debounce';
import { Button } from '../../../../components/ui/button';
import { Input } from '../../../../components/ui/input';
import { Label } from '../../../../components/ui/label';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../../../components/ui/tooltip';
import { cn } from '../../../../lib/utils';

interface CryptoToRubCalculatorProps {
  initialCryptoRate: number;
  cryptoSymbol: string;
  className?: string;
}

const formatNumber = (value: number | string | undefined, isCrypto: boolean): string => {
  const numValue = typeof value === 'string' ? parseFloat(value.replace(/,/g, '.')) : value;

  if (numValue === undefined || numValue === null || isNaN(numValue)) {
    return '';
  }

  return isCrypto
    ? parseFloat(numValue.toFixed(8)).toString()
    : numValue.toLocaleString('ru-RU', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
};

export default function CryptoToRubCalculator({
  initialCryptoRate,
  cryptoSymbol,
  className = '',
}: CryptoToRubCalculatorProps) {
  const [direction, setDirection] = useState<'cryptoToRub' | 'rubToCrypto'>('cryptoToRub');
  const [cryptoValue, setCryptoValue] = useState('1');
  const [rubValue, setRubValue] = useState('');
  const [activeInput, setActiveInput] = useState<'crypto' | 'rub'>('crypto');
  const [debouncedCryptoRate] = useDebounce(initialCryptoRate, 300);

  const convertValues = useCallback(
    (source: 'crypto' | 'rub', value: string) => {
      if (debouncedCryptoRate <= 0) return;

      const numValue = parseFloat(value.replace(/,/g, '.')) || 0;

      if (source === 'crypto') {
        setRubValue(formatNumber(numValue * debouncedCryptoRate, false));
      } else {
        setCryptoValue(formatNumber(numValue / debouncedCryptoRate, true));
      }
    },
    [debouncedCryptoRate],
  );

  const handleCryptoChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      if (/^[0-9.,]*$/.test(value)) {
        setCryptoValue(value);
        setActiveInput('crypto');
        convertValues('crypto', value);
      }
    },
    [convertValues],
  );

  const handleRubChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      if (/^[0-9.,]*$/.test(value)) {
        setRubValue(value);
        setActiveInput('rub');
        convertValues('rub', value);
      }
    },
    [convertValues],
  );

  const toggleDirection = useCallback(() => {
    const currentCryptoNum = parseFloat(cryptoValue.replace(/,/g, '.')) || 0;
    const currentRubNum = parseFloat(rubValue.replace(/,/g, '.')) || 0;

    const newDirection = direction === 'cryptoToRub' ? 'rubToCrypto' : 'cryptoToRub';
    setDirection(newDirection);
    setCryptoValue(formatNumber(currentRubNum, true));
    setRubValue(formatNumber(currentCryptoNum, false));
    setActiveInput(newDirection === 'rubToCrypto' ? 'rub' : 'crypto');
  }, [cryptoValue, rubValue, direction]);

  useEffect(() => {
    if (activeInput === 'crypto') {
      convertValues('crypto', cryptoValue);
    } else {
      convertValues('rub', rubValue);
    }
  }, [debouncedCryptoRate, convertValues, activeInput, cryptoValue, rubValue]);

  return (
    <div
      className={cn('space-y-4 p-4 bg-slate-800/50 rounded-lg border border-slate-700', className)}
    >
      <h2 className="text-xl font-semibold text-white">
        Конвертер {direction === 'cryptoToRub' ? cryptoSymbol : 'RUB'} ↔{' '}
        {direction === 'cryptoToRub' ? 'RUB' : cryptoSymbol}
      </h2>

      <div className="space-y-3">
        <div className="space-y-1">
          <Label htmlFor="input-top" className="text-slate-300">
            {direction === 'cryptoToRub' ? cryptoSymbol : 'Российский рубль (RUB)'}
          </Label>
          <Input
            id="input-top"
            type="text"
            inputMode="decimal"
            value={direction === 'cryptoToRub' ? cryptoValue : rubValue}
            onChange={direction === 'cryptoToRub' ? handleCryptoChange : handleRubChange}
            placeholder="0"
            className="text-white bg-slate-700/50 border-slate-600 focus-visible:ring-blue-500 text-lg"
            aria-label={`Сумма ${direction === 'cryptoToRub' ? cryptoSymbol : 'RUB'}`}
          />
        </div>

        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex justify-center my-1">
                <Button
                  onClick={toggleDirection}
                  variant="outline"
                  size="icon"
                  className="rounded-full p-2 bg-slate-700 border-slate-600 text-slate-300 hover:bg-slate-600 hover:text-blue-400 transition-all hover:scale-110 focus-visible:ring-blue-500"
                  aria-label="Поменять направление конвертации"
                >
                  <RiArrowUpDownFill className="h-5 w-5" />
                </Button>
              </div>
            </TooltipTrigger>
            <TooltipContent className="bg-slate-700 text-white border-slate-600">
              Поменять направление
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        <div className="space-y-1">
          <Label htmlFor="input-bottom" className="text-slate-300">
            {direction === 'cryptoToRub' ? 'Российский рубль (RUB)' : cryptoSymbol}
          </Label>
          <Input
            id="input-bottom"
            type="text"
            inputMode="decimal"
            value={direction === 'cryptoToRub' ? rubValue : cryptoValue}
            onChange={direction === 'cryptoToRub' ? handleRubChange : handleCryptoChange}
            placeholder="0"
            className="text-white bg-slate-700/50 border-slate-600 focus-visible:ring-blue-500 text-lg"
            aria-label={`Сумма ${direction === 'cryptoToRub' ? 'RUB' : cryptoSymbol}`}
          />
        </div>
      </div>

      <div className="text-sm text-slate-400 mt-2 text-center sm:text-left">
        <p>
          Текущий курс:{' '}
          <span className="font-medium text-white ml-1 tabular-nums">
            1 {cryptoSymbol} ≈ {formatNumber(debouncedCryptoRate, false)} RUB
          </span>
        </p>
      </div>
    </div>
  );
}
